import { ChangeEvent, ReactElement, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { ReactComponent as CloseIcon } from "components/Icons/close.svg";
import { ReactComponent as SearchIcon } from "components/Icons/search.svg";
import { AperitifPreset, aperitifMenuApi } from "lib/api/aperitifMenu";
import { AperitifPresetListItem } from "./AperitifPresetListItem";
import { Navigation } from "components/UIComponents/Navigation";
import { SubTitle } from "components/UIComponents/SubTitle";
import { PrimaryButton } from "components/UIComponents/PrimaryButton";
import { Checkbox } from "components/UIComponents/Checkbox";

interface AperitifPickerProps {
    language: string;
    onPick: (presets: AperitifPreset[]) => void;
    onClose: () => void;
}

export function AperitifPicker({ language, onPick, onClose }: AperitifPickerProps): ReactElement {
    const intl = useIntl();
    const [presets, setPresets] = useState<AperitifPreset[]>([]);
    const [selected, setSelected] = useState<Record<string, AperitifPreset>>({});
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const loadPresets = useCallback(() => {
        setLoading(true);
        aperitifMenuApi.listAperitifPresets()
            .then(data => {
                const sorted = [...data].sort((a, b) =>
                    (a.body.name?.["de"] ?? "").localeCompare(b.body.name?.["de"] ?? "", "de")
                );
                setPresets(sorted);
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { loadPresets(); }, [loadPresets]);

    const filtered = presets.filter(p => {
        const term = searchTerm.toLowerCase();
        const name = (p.body.name?.[language] ?? p.body.name?.["de"] ?? "").toLowerCase();
        return name.includes(term);
    });

    const toggleSelect = (preset: AperitifPreset, checked: boolean) => {
        setSelected(prev => {
            if (checked) return { ...prev, [preset.id]: preset };
            const { [preset.id]: _, ...rest } = prev;
            return rest;
        });
    };

    const handlePick = () => {
        onPick(Object.values(selected));
        onClose();
    };

    const noPicked = Object.keys(selected).length;

    return (
        <div className="w-full h-full flex flex-col items-stretch bg-white">
            <div className="flex-none">
                <Navigation
                    left={<SubTitle><FormattedMessage id="aperitifpicker.navigation.title" /></SubTitle>}
                    right={<>
                        <div className="flex-auto font-semibold py-4 leading-snug text-right">
                            {noPicked > 0 && (
                                <span className="flex-none">
                                    <FormattedMessage id="aperitifpicker.results.noPicked" values={{ noPicked }} />
                                </span>
                            )}
                        </div>
                        <PrimaryButton onClick={handlePick} disabled={noPicked === 0}>
                            <FormattedMessage id="aperitifpicker.navigation.chose" />
                        </PrimaryButton>
                        <span />
                        <button onClick={onClose}><CloseIcon /></button>
                    </>}
                />
            </div>

            <div className="flex-auto overflow-y-auto px-11 py-4">
                <div className="relative mb-4">
                    <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" />
                    <input
                        type="text"
                        placeholder={intl.formatMessage({ id: "aperitif.search.placeholder" })}
                        className="w-full pl-12 pr-4 py-3 bg-warm-gray rounded-lg border-0 focus:outline-none focus:ring-2 focus:ring-primary"
                        value={searchTerm}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                    />
                </div>

                {loading && (
                    <div className="flex items-center justify-center py-8 text-sm">
                        <FormattedMessage id="aperitif.list.loading" />
                    </div>
                )}

                {!loading && (
                    <ul className="flex flex-col gap-1">
                        {filtered.map(preset => {
                            const isSelected = preset.id in selected;
                            return (
                                <li key={preset.id}>
                                    <AperitifPresetListItem
                                        preset={preset}
                                        language={language}
                                        onClick={p => toggleSelect(p, !isSelected)}
                                        left={
                                            <div className="flex-none px-1 shrink-0 w-[32px]">
                                                <Checkbox
                                                    checked={isSelected}
                                                    setChecked={checked => toggleSelect(preset, checked)}
                                                />
                                            </div>
                                        }
                                    />
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}
